import { ArrowDown, ArrowUp, Minus } from 'lucide-react'
import { Chip } from '@/components/ui'
import { classifyMargin } from '@/lib/utils/margin-thresholds'
import type { CustomerPricingRow, PricingRow } from '../api/types'

/**
 * Shared row-identity + cell-rendering helpers for the Product↔Customer
 * Pricing module. Originally lived inline in ProductCustomerPricingPage —
 * pulled out here so CompareDrawer (Compare feature) can reuse the exact
 * same margin-tone / trend-chip rendering instead of drifting from the
 * main table's. Single source of truth for both surfaces.
 */

/** Row-identity type guard: forward mode returns CustomerPricingRow
 *  (has customer_id), reverse mode returns ProductPricingRow (has
 *  product_id). */
// eslint-disable-next-line react-refresh/only-export-components
export function isCustomerRow(row: PricingRow): row is CustomerPricingRow {
  return 'customer_id' in row
}

/** Stable identity for a pricing row — same key DataTable's `getRowId`
 *  and the page's selection state (and CompareDrawer's remove callback)
 *  key off of. */
// eslint-disable-next-line react-refresh/only-export-components
export function pricingRowId(row: PricingRow): string {
  return isCustomerRow(row) ? String(row.customer_id) : String(row.product_id)
}

export function MarginCell({ value }: { value: number | null }) {
  if (value === null) return <span className="text-tertiary">—</span>
  const bucket = classifyMargin(value)
  // Chip has no 'negative' variant — MarginIndicator paints
  // negative the same as danger (both red), so fold it in here too.
  const tone = bucket === 'negative' ? 'danger' : bucket
  return (
    <Chip size="xs" variant={tone}>
      {value.toFixed(1)}%
    </Chip>
  )
}

export function TrendDeltaChip({ trend }: { trend: NonNullable<CustomerPricingRow['margin_trend']> }) {
  if (trend.direction === 'up') {
    return (
      <Chip size="xs" variant="success">
        <ArrowUp size={10} /> +{trend.delta_pct.toFixed(1)}pts
      </Chip>
    )
  }
  if (trend.direction === 'down') {
    return (
      <Chip size="xs" variant="danger">
        <ArrowDown size={10} /> {trend.delta_pct.toFixed(1)}pts
      </Chip>
    )
  }
  return (
    <Chip size="xs" variant="neutral">
      <Minus size={10} /> flat
    </Chip>
  )
}

// Inverted tone vs. TrendDeltaChip: rising vendor cost is bad for margin
// (danger), falling cost is good (success). Icon still tracks the actual
// direction of the number; only the sentiment color flips.
export function CostTrendChip({ trend }: { trend: NonNullable<CustomerPricingRow['cost_trend_yoy']> }) {
  if (trend.direction === 'up') {
    return (
      <Chip size="xs" variant="danger">
        <ArrowUp size={10} /> +{trend.pct_change.toFixed(1)}%
      </Chip>
    )
  }
  if (trend.direction === 'down') {
    return (
      <Chip size="xs" variant="success">
        <ArrowDown size={10} /> {trend.pct_change.toFixed(1)}%
      </Chip>
    )
  }
  return (
    <Chip size="xs" variant="neutral">
      <Minus size={10} /> flat
    </Chip>
  )
}
